import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren, signal, computed } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  faCircleExclamation,
  faDiagramSuccessor,
  faPrint,
  faTriangleExclamation,
  faQuestionCircle
} from '@fortawesome/free-solid-svg-icons';

import { ManagementUser } from '../../../core/models/management-user.model';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { District } from '../../../core/models/district.model';
import { School } from '../../../core/models/school.model';
import { SubscriptionTypes } from '../../../core/models/subscription-types.model';
import { Student } from '../../../core/models/student.model';
import { Objective, ObjectiveScore } from '../../../core/models/objective.model';
import { ReportsService } from '../../../core/services/reports.service';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData, ChartType, Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels' ;
import { PrintService } from 'src/app/core/services/print.service';
import * as Sentry from '@sentry/angular';
import { objectiveIntroSteps } from './objective.intro-steps';
import { canViewCoTeacherFilter, canViewSchoolFilter } from '../../utils';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';

type ObjectiveGrowthCategory =
  'Decoding Words' |
  'Using Syllables' |
  'Recognizing Words (untimed)' |
  'Recognizing Words (timed)';

@Component({
    selector: 'wf-objective-prepost',
    templateUrl: './objective.component.html',
    styleUrls: ['./objective.component.css'],
    standalone: false
})
export class ObjectivePrePostComponent implements OnInit {
  DECODING = 0;
  SYLLABLES = 1;
  UNTIMED_RECOGNIZING = 2;
  TIMED_RECOGNIZING = 3;
  CATEGORY_DECODING: ObjectiveGrowthCategory = 'Decoding Words';
  CATEGORY_SYLLABLES: ObjectiveGrowthCategory = 'Using Syllables';
  CATEGORY_UNTIMED_RECOGNIZING: ObjectiveGrowthCategory = 'Recognizing Words (untimed)';
  CATEGORY_TIMED_RECOGNIZING: ObjectiveGrowthCategory = 'Recognizing Words (timed)';

  currentUser: ManagementUser | null = null ;
  showSchoolFilter: boolean = false ;
  hideSharedStudents: boolean = false;
  showCoTeacherFilter: boolean = false ;
  schoolFilterError: string = '' ;
  messageTitle: string = '' ;
  message: string = '' ;
  messageStatus: string = '' ;
  fullProduct: string = SubscriptionTypes.FullProduct ;
  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  students: Student[] = [] ;
  selectedStudent: Student | null = null ;
  navigateStudentId: number = -1 ;
  selectedObjective: Objective | null = null ;
  selectedCategory: { label: ObjectiveGrowthCategory, value: number } | null = null ;
  reportsIcon = CATEGORY_ICONS['class-reports'];
  printIcon = faPrint ;
  objectiveIcon = faDiagramSuccessor ;
  warningIcon = faTriangleExclamation ;
  errorIcon = faCircleExclamation ;
  helpIcon = faQuestionCircle;
  tourSteps = objectiveIntroSteps;

  objectiveColumns: string[][] = [
    [ 'Decoding Words'],
    [ 'Using Syllables' ],
    [ 'Recognizing Words', '(untimed)' ],
    [ 'Recognizing Words', '(timed)' ],
  ] ;
  growthCategories = [
    { label: this.CATEGORY_DECODING, value: this.DECODING },
    { label: this.CATEGORY_SYLLABLES, value: this.SYLLABLES },
    { label: this.CATEGORY_UNTIMED_RECOGNIZING, value: this.UNTIMED_RECOGNIZING },
    { label: this.CATEGORY_TIMED_RECOGNIZING, value: this.TIMED_RECOGNIZING },
  ]

  objectiveChartData: ChartData<'bar'> = {
    labels: this.objectiveColumns,
    datasets: [
      {
        label: 'Pretest',
        data: [],
        backgroundColor: '#33AAE1',
        borderRadius: 4,
        hoverBackgroundColor: '#258CBB',
        hoverBorderColor: '#146184',
        barThickness: 60,
      },
      {
        label: 'Posttest',
        data: [],
        backgroundColor: '#0054A6',
        borderRadius: 4,
        hoverBackgroundColor: '#003E79',
        hoverBorderColor: '#01294E',
        barThickness: 60,
      }
    ]
  } ;

  objectives = signal<{ [k: string]: Objective }>({});
  growthScores = signal<{
    pre: { id: number, value: number, inProgress: boolean, notStarted: boolean }[],
    post: { id: number, value: number, inProgress: boolean, notStarted: boolean }[],
  }>({
    pre: [],
    post: []
  });

  objectiveGrowthColumns = computed<string[][]>(
    () => {
      const objectiveKeys =  Object.keys(this.objectives());
      const [o1, o2, o3, o4, o5, o6] = objectiveKeys.sort().map(k => this.objectives()[k]);

      const dateRange = (o: any) => o?.complete ? ['', o.formattedStartDate + ' -', o.formattedEndDate] : [];
      return [
       [ 'Objective 1', 'Short Vowels' ].concat(dateRange(o1)),
       [ 'Objective 2', 'Long Vowels', 'with Silent e' ].concat(dateRange(o2)),
       [ 'Objective 3', 'Short Vowels', 'and Long Vowel', 'Digraphs' ].concat(dateRange(o3)),
       [ 'Objective 4', 'Long Vowel Digraphs,', 'and R Controlled', 'Vowels' ].concat(dateRange(o4)),
       [ 'Objective 5', 'Diphthongs, Digraphs,', 'R Controlled Vowels' ].concat(dateRange(o5)),
       [ 'Objective 6', 'Mixed Vowels' ].concat(dateRange(o6)),
     ];
    }
  );

  objectiveGrowthChartData = computed<ChartData<'bar', ObjectiveScore[]>>(
    () => ({
      labels: this.objectiveGrowthColumns(),
      datasets: [
        {
          label: 'Pretest',
          data: this.growthScores().pre,
          backgroundColor: '#80C342',
          borderRadius: 4,
          hoverBackgroundColor: '#629A2E',
          hoverBorderColor: '#40681B',
          barThickness: 60,
        },
        {
          label: 'Posttest',
          data: this.growthScores().post,
          backgroundColor: '#007236',
          hoverBackgroundColor: '#004521',
          hoverBorderColor: '#002914',
          borderRadius: 4,
          barThickness: 60,
        }
      ],
    })
  );
  chartPlugins = [ ChartDataLabels ];
  chartType: ChartType = 'bar' ;
  chartOpts: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          lineWidth: 0
        }
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          major: {
            enabled: true
          },
          callback: (value) => {
            const num = typeof value === 'string' ? parseInt(value, 10) : value;
            return num % 20 === 0 ? `${num}` : '';
          }
        },
        title: {
          display: true,
          text: '% Correct',
          font: {
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
            size: 14,
          }
        }
      }
    },
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        color: 'white',
        anchor: 'end',
        align: 'start',
        offset: 25,
        font: {
          family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
          size: 14,
        },
      },
    }
  } ;
  growthChartOpts: ChartConfiguration['options'] = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          lineWidth: 0
        }
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          major: {
            enabled: true
          },
          callback: (value) => {
            const num = typeof value === 'string' ? parseInt(value, 10) : value;
            return num % 20 === 0 ? `${num}` : '';
          }
        },
        title: {
          display: true,
          text: '% Correct',
          font: {
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
            size: 14,
          }
        }
      }
    },
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
      datalabels: {
        display: true,
        textAlign: 'center',
        anchor: (context) => {
          const index = context.dataIndex ;
          const value: any = context.dataset.data[index] ;

          return (value.inProgress || value.notStarted) ? 'center' : 'end';
        },
        align: (context) => {
          const index = context.dataIndex ;
          const value: any = context.dataset.data[index] ;

          return (value.inProgress || value.notStarted) ? 'top' : 'start';
        },
        offset: 25,
        font: (context) => {
          const index = context.dataIndex ;
          const value: any = context.dataset.data[index] ;

          if (value.inProgress || value.notStarted)
          {
            return {
              size: 20,
              weight: 'bold',
            };
          }

          return {
            family: '"Gotham SSm A", "Gotham SSm B", "Arial", "Helvetica", sans-serif',
            size: 14,
          }
        },
        color: (context) => {
          const index = context.dataIndex ;
          const value: any = context.dataset.data[index] ;

          if (value.inProgress)
          {
            return '#80C342' ;
          }
          else if (value.notStarted)
          {
            return '#AAAAAA' ;
          }

          return '#FFFFFF' ;
        },
        formatter: (value, context) => {
          // The only data labels we want to display on our bar chart are 'In Progress' or 'Not Started'
          // but because we have 2 datasets (one for pre and one for post), we *only* want to display one
          // datalabel if we are not started or in progress, so here we test the datasetindex we are iterating
          // through and only display a datalabel if we are in the first data set (pre)
          // NOTE: The whitespace is needed here so it appears centered over both datasets (hack)
          if (value.inProgress && context.datasetIndex === 0) {
            return '               In\n               Progress' ;
          }
          else if (value.notStarted && context.datasetIndex === 0) {
            return '               Not\n               Started' ;
          }
          else if (!value.inProgress && !value.notStarted) {
            return value.value;
          }

          return '' ;
        }
      },
    },
    parsing: {
      xAxisKey: 'id',
      yAxisKey: 'value',
    },
  } ;

  @ViewChild('printContent') printContent!: ElementRef<HTMLElement> ;
  @ViewChild('objectiveChartCanvas') objectiveChartCanvas!: ElementRef<HTMLCanvasElement> ;
  @ViewChild('objectiveGrowthChartCanvas') objectiveGrowthChartCanvas!: ElementRef<HTMLCanvasElement> ;
  @ViewChild('objectiveChartImg') objectiveChartImg!: ElementRef<HTMLImageElement> ;
  @ViewChild('objectiveGrowthChartImg') objectiveGrowthChartImg!: ElementRef<HTMLImageElement> ;
  @ViewChildren(BaseChartDirective) charts!: QueryList<BaseChartDirective> ;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reportsService: ReportsService,
    private sessionStorageService: SessionStorageService,
    private printService: PrintService
  ) {
    this.navigateStudentId = parseInt(this.router.getCurrentNavigation()?.extras?.state?.['studentId']) ;
    if (isNaN(this.navigateStudentId)) this.navigateStudentId = -1 ;
  }

  ngOnInit(): void {
    let resolvedData = this.route.snapshot.data['resolveData'] ;
    this.currentUser = this.sessionStorageService.userData() ;
    this.showSchoolFilter = canViewSchoolFilter(this.currentUser);
    this.showCoTeacherFilter = canViewCoTeacherFilter(this.currentUser);

    if (resolvedData.erred)
    {
      this.schoolFilterError = resolvedData.message ;
    }
    else
    {
      this.students = this.filterCoAccessStudents(resolvedData.students) ;
      this.checkForSharedStudents() ;

      // If we had a student ID passed to us in nav, attempt to find the student and if we find them, get data and display
      if (this.navigateStudentId > 0)
      {
        this.selectedStudent = this.students.find((student) => student.userID === this.navigateStudentId) || null ;
        if (this.selectedStudent)
        {
          this.updateStudent() ;
        }
        else
        {
          this.message = 'The Objective data for the requested Student could not be loaded, showing the first available Student' ;
          this.messageTitle = 'Requested Student Not Found' ;
          this.messageStatus = 'warning' ;
        }
      }
      else if (this.students.length)
      {
        // If we get here, we did not have a student passed to us on navigation, so select the first student and display
        this.selectedStudent = this.students[0] ;
        this.updateStudent();
      }
    }

    // Filter data
    this.districts = this.route.snapshot.data['filterData'].districts ;
    this.schools = this.route.snapshot.data['filterData'].schools ;
    this.teachers = this.route.snapshot.data['filterData'].teachers ;

    if (this.currentUser!.isFILUser() || this.currentUser!.isDistrictUser())
    {
      // The FIL user and District user roles can select all schools
      this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    }

    if (canViewSchoolFilter(this.currentUser))
    {
      // The FIL user and District and School user roles can select all teachers
      this.teachers.unshift(ManagementUser.getGenericUser()) ;
    }
  }

  private checkForSharedStudents() {
    if (this.showCoTeacherFilter) {
      const hasAnySharedStudents = this.students.some(student => student.teacherID !== this.reportsService.getSelectedTeacherForReports().userID) ;
      this.showCoTeacherFilter = hasAnySharedStudents;
    }
    else {
      this.showCoTeacherFilter = false;
    }
  }

  filterSchoolTeacher() {
    this.reportsService.getObjectiveDataStudents().subscribe({
      next: (data: any) => {
        if (data.erred)
        {
          this.schoolFilterError = data.message ;
          this.selectedObjective = null ;
          this.selectedCategory = null ;
        }
        else
        {
          this.schoolFilterError = '' ;
          this.students = this.filterCoAccessStudents(data.students) ;

          if (this.students.length)
          {
            this.selectedStudent = this.students[0] ;
            this.updateStudent() ;
          }
          else
          {
            this.selectedStudent = null ;
            this.selectedObjective = null ;
            this.selectedCategory = null ;
          }
        }
      },
      error: (err: any) => {
        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'system-objective',
            action: 'get-objective-data',
          }
        }) ;
      }
    }) ;
  }

  filterCoAccessStudents(students: Student[]) {
    const currentUser = this.sessionStorageService.userData() ;
    return currentUser?.isTeacherUser() && !this.hideSharedStudents ? students : students.filter(student => student.teacherID === this.reportsService.getSelectedTeacherForReports().userID) ;
  }

  updateStudent() {
    let schoolId = this.reportsService.getSelectedSchoolForReports().schoolID ;
    let teacherId = this.reportsService.getSelectedTeacherForReports().userID ;

    // Clear out any old loading messages/warnings
    this.message = '' ;
    this.messageTitle = '' ;
    this.messageStatus = '' ;
    this.reportsService.getObjectivePrePostPerformanceData(schoolId, teacherId, this.selectedStudent!.userID).subscribe({
      next: (objectiveData) => {
        this.processObjectiveData(objectiveData) ;
      },
      error: (err) => {
        this.message = 'There was an error trying to load the Objective data for the selected Student, try again later or ask us for help' ;
        this.messageTitle = 'Error Loading Data' ;
        this.messageStatus = 'error' ;

        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'system-objective',
            schoolId: schoolId,
            teacherId: teacherId,
            studentId: (this.selectedStudent) ? this.selectedStudent.userID : 'no-student-selected',
          }
        }) ;
      }
    }) ;
  }

  updateObjectiveChart() {
    let objData = this.createObjectiveDataSet(this.selectedObjective?.taskData ?? {}) ;
    this.objectiveChartData.datasets[0].data = objData['pretest'] ;
    this.objectiveChartData.datasets[1].data = objData['posttest'] ;
    if (this.charts && this.charts.first) this.charts.first.update() ;
  }

  updateObjectiveGrowthChart() {
    const objectiveGrowthData = this.createObjectiveGrowthDataSet(this.selectedCategory?.label ?? null, Object.values(this.objectives()));
    this.growthScores.set({
      pre: objectiveGrowthData.preData,
      post: objectiveGrowthData.postData,
    });
    if (this.charts && this.charts.last) this.charts.last.update() ;
  }

  toggleSharedStudents() {
    this.reportsService.getObjectiveDataStudents().subscribe({
      next: (data: any) => {
        if (data.erred)
        {
          this.schoolFilterError = data.message ;
          this.selectedObjective = null ;
        }
        else
        {
          this.schoolFilterError = '' ;
          this.students = this.filterCoAccessStudents(data.students) ;

          if (this.students.length)
          {
            this.selectedStudent = this.students[0] ;
            this.updateStudent() ;
          }
          else
          {
            this.selectedStudent = null ;
            this.selectedObjective = null ;
          }
        }
      }
    })

  }

  trackByStudentId(index: number, obj: Student) {
    return obj.userID ;
  }

  printReport() {
    // Convert charts to images before print:
    this.reportsService.convertCanvasToImage(this.objectiveChartCanvas.nativeElement, this.objectiveChartImg.nativeElement);
    this.reportsService.convertCanvasToImage(this.objectiveGrowthChartCanvas.nativeElement, this.objectiveGrowthChartImg.nativeElement);

    let school = this.reportsService.getSelectedSchoolForReports().name;
    let teacher = this.reportsService.getSelectedTeacherForReports();
    let teacherName = `${teacher.firstName} ${teacher.lastName}`;
    this.printService.openPrintWindow(this.printContent.nativeElement, school, teacherName);
  }

  private processObjectiveData(objectiveData: { [ k: string ]: Objective }) {
    if (Object.keys(objectiveData).length === 0) return ;

    let objectiveCnt = 0 ;
    const objectives: { [ k: string ]: Objective } = {} ;

    Object.keys(objectiveData).forEach((objectiveNum) => {
      if (objectiveNum !== 'cumulative')
      {
        objectives[objectiveNum] = objectiveData[objectiveNum] ;
        objectiveCnt++ ;
      }
    }) ;
    this.objectives.set(objectives);
    this.selectedObjective = objectives[`${objectiveCnt}`] ;
    this.selectedCategory = this.growthCategories[0];

    // Now, load our datasets for Objective charts
    if (this.selectedObjective)
    {
      this.updateObjectiveChart() ;
    }

    // Now, load our datasets for Objective Growth charts
    if (this.selectedCategory) {
      this.updateObjectiveGrowthChart();
    }
  }

  private createObjectiveDataSet(taskData: any) {
    let predata: number[] = [] ;
    let postdata: number[] = [] ;

    // Create our datasets in order
    this.objectiveColumns.forEach((area) => {
      let tag = area.join(' ') ;
      if (taskData[tag])
      {
        predata.push(taskData[tag].pretest.score);
        postdata.push(taskData[tag].posttest.score);
      }
    }) ;

    return {
      pretest: predata,
      posttest: postdata,
    } ;
  }

  private createObjectiveGrowthDataSet(category: ObjectiveGrowthCategory | null, completedObjectives: Objective[]) {
    if (!category) return { preData: [], postData: [] };

    const objectiveMap = new Map<number, Objective>(
      completedObjectives.map(obj => [obj.objectiveNumber, obj])
    );

    // For all our growth columns, set our pre and post values
    const preData: ObjectiveScore[] = [];
    const postData: ObjectiveScore[] = [];
    for (let i = 0; i < this.objectiveGrowthColumns().length; i++) {
      const id = i + 1;
      const objective = objectiveMap.get(id);
      const taskCategory = objective?.taskData?.[category];

      if (objective && taskCategory) {
        const inProgress = !objective.complete;
        preData.push({ id, value: inProgress ? 0 : taskCategory.pretest.score, inProgress, notStarted: false });
        postData.push({ id, value: inProgress ? 0 : taskCategory.posttest.score, inProgress, notStarted: false });
      } else {
        // Fallback default state if no matching objective data exists
        preData.push({ id, value: 0, inProgress: false, notStarted: true });
        postData.push({ id, value: 0, inProgress: false, notStarted: true });
      }
    }

    return { preData, postData };
  }
}
